import type { NavigateFunction } from 'react-router';
import logger from '@ayocore/platform/common/helpers/logger';
import { extractErrorMessage } from '@ayocore/platform/client/utils';
import type { AwaitableRpcClient } from '@ayocore/platform/server/rpc/types';
import type {
  PartnerRepository,
  PartnerStats,
  PartnerChartPoint,
} from '../../../../server/repositories/PartnerRepository';
import type { ContactPersonRepository } from '../../../../server/repositories/ContactPersonRepository';
import type { LeadRepository } from '../../../../server/repositories/LeadRepository';
import type { DealRepository } from '../../../../server/repositories/DealRepository';
import { assistant } from '@ayocore/llm/client/assistant';
import type { Partner } from '../../../../schemas/partner.schema';
import type { ContactPerson } from '../../../../schemas/contact-person.schema';
import type { ContactChannel } from '../../../../schemas/contact-channel.schema';
import type { Lead } from '../../../../schemas/lead.schema';
import type { LeadStatus } from '../../../../schemas/lead-status.schema';
import type { Deal } from '../../../../schemas/deal.schema';
import type { Segment } from '../../../../schemas/segment.schema';
import type { Tag } from '../../../../schemas/tag.schema';

/**
 * Расширенный тип партнера для безопасного рендеринга relation-объектов
 */
export type PartnerExtended = Partner & {
  manager?: { id?: number; name?: string; email?: string } | null;
  sales_area?: { id?: number; name?: string } | null;
  /** Роли партнёра (клиент/поставщик/подрядчик/перевозчик) — из withRoleList(). */
  roles?: { id: number; code: string; name: string; description?: string | null }[];
};

/**
 * Лид с подгруженными relation-полями, необходимыми для визуализации
 * воронки статусов в карточке партнёра.
 */
export type PartnerLeadExtended = Lead & {
  lead_status?: LeadStatus | null;
  lead_type?: { id?: number; slug?: string; name?: string } | null;
};

/**
 * Состояние контроллера PartnerView
 */
export interface PartnerViewState {
  partner: PartnerExtended | null;
  partnerStats: PartnerStats | null;
  partnerChart: PartnerChartPoint[];
  contactPersons: (ContactPerson & { is_primary?: boolean; role?: string })[];
  leads: PartnerLeadExtended[];
  deals: Deal[];
  contactChannels: ContactChannel[];
  segments: Segment[];
  tags: Tag[];
  purchasesCount: number | null;
  loading: boolean;
  error: string | null;
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    /** Точечное право на архивацию/разархивирование (canUpdate(id, 'archived')). */
    canArchive: boolean;
    /**
     * Точечное право на смену менеджера (canUpdate(id, 'manager')).
     * По умолчанию разрешено — ограничения задаёт инстанс своим guard'ом.
     */
    canEditManager: boolean;
    /**
     * Право объединять дублей (RBAC-действие mergeDuplicates).
     * Не входит в базовый набор CRM-ролей — выдаётся инстансом точечно.
     */
    canMerge: boolean;
    loading: boolean;
  };
  modals: {
    edit: boolean;
    delete: boolean;
  };
}

/**
 * PartnerViewController - контроллер для управления состоянием PartnerView
 *
 * Следует паттерну NotificationController:
 * - Внешнее состояние (не React state)
 * - Простая система подписки через колбэки
 * - Методы subscribe и getSnapshot для useSyncExternalStore
 */
export class PartnerViewController {
  // Внешнее состояние (не React state)
  private state: PartnerViewState = {
    partner: null,
    partnerStats: null,
    partnerChart: [],
    contactPersons: [],
    leads: [],
    deals: [],
    contactChannels: [],
    segments: [],
    tags: [],
    purchasesCount: null,
    loading: true,
    error: null,
    permissions: {
      canEdit: false,
      canDelete: false,
      canArchive: false,
      canEditManager: false,
      canMerge: false,
      loading: true,
    },
    modals: {
      edit: false,
      delete: false,
    },
  };

  // Система подписки через колбэки
  private listeners: Set<() => void> = new Set();

  // Подписка для useSyncExternalStore
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // Получить снимок текущего состояния (для useSyncExternalStore)
  // Важно: useSyncExternalStore требует референциальной стабильности снапшота,
  // поэтому возвращаем тот же объект состояния без копирования.
  getSnapshot = (): PartnerViewState => this.state;

  // Уведомить всех подписчиков об изменении состояния
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        logger.error('Error in PartnerViewController listener:', error);
      }
    });
  }

  // Вспомогательный метод: неизменяемое обновление состояния
  private replaceState(patch: Partial<PartnerViewState>): void {
    this.state = { ...this.state, ...patch };
  }

  /**
   * Загрузить данные партнера
   */
  async load(
    partnerId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    partnerRepository: AwaitableRpcClient<PartnerRepository>,
    contactPersonRepository: AwaitableRpcClient<ContactPersonRepository>,
    leadRepository: AwaitableRpcClient<LeadRepository>,
    dealRepository: AwaitableRpcClient<DealRepository>
  ): Promise<void> {
    if (!partnerId || isNaN(partnerId)) {
      this.replaceState({
        error: 'Неверный ID партнера',
        loading: false,
      });
      this.notifyListeners();
      return;
    }

    try {
      this.replaceState({
        loading: true,
        error: null,
        purchasesCount: null,
      });
      this.notifyListeners();

      // Загружаем данные партнера (+ роли — нужны для проверки «клиент ли партнёр»)
      const partnerResult = await partnerRepository.withRoleList().findById(partnerId);

      if (!partnerResult) {
        this.replaceState({
          error: `Партнер с ID ${partnerId} не найден`,
          loading: false,
        });
        if (!isModal) {
          setTabTitle(`Партнер #${partnerId} - Не найден`);
        }
        this.notifyListeners();
        return;
      }

      const partnerExtended = partnerResult as PartnerExtended;

      // Загружаем связанные данные параллельно
      const [
        contactPersonsResult,
        leadsResult,
        dealsResult,
        contactChannelsResult,
        segmentsResult,
        tagsResult,
        partnerChartResult,
      ] = await Promise.all([
        // Get contact persons for this partner with aggregated emails/phones
        contactPersonRepository
          .filterByPartner(partnerId)
          .withContactChannels()
          .withContactChannelList()
          .orderBy('is_active', 'desc')
          .orderBy('is_primary', 'desc')
          .orderBy('name', 'asc')
          .fetch(),
        // Get leads for this partner — with lead_status (для is_final) и lead_type (для funnel)
        leadRepository.filterByPartner(partnerId).withRelations().fetch(),
        // Get deals for this partner
        dealRepository
          .filterByPartner(partnerId)
          .withRelations()
          .orderBy('created_at', 'desc')
          .limit(10)
          .fetch(),
        // Get contact channels for this partner
        partnerRepository.getPartnerContacts(partnerId),
        // Get partner segments
        partnerRepository.getSegments(partnerId),
        // Get partner tags
        partnerRepository.getTags(partnerId),
        // Get chart data for last 12 months
        partnerRepository.getPartnerChart12m(partnerId),
      ]);

      let partnerStats: PartnerStats | null = null;
      try {
        partnerStats = await partnerRepository.getPartnerStats(partnerId);
      } catch (statsError) {
        logger.error('Failed to fetch partner stats:', statsError);
      }

      this.replaceState({
        partner: partnerExtended,
        partnerStats,
        partnerChart: partnerChartResult || [],
        contactPersons: contactPersonsResult || [],
        leads: (leadsResult || []) as PartnerLeadExtended[],
        deals: dealsResult || [],
        contactChannels: contactChannelsResult || [],
        segments: segmentsResult || [],
        tags: tagsResult || [],
        loading: false,
      });

      // Устанавливаем заголовок вкладки только если не в модалке
      if (!isModal) {
        setTabTitle(partnerExtended.name as string);
      }

      // Отправляем UI Context для LLM ассистента
      try {
        const entity = await partnerRepository.getEntity(partnerId);
        if (entity) {
          await assistant.setEntityContext(entity);
          logger.debug('🖥️ [PartnerViewController] UI Context set', { entity, partnerId });
        }
      } catch (err) {
        // Игнорируем ошибку - UI Context не критичен
        logger.debug('⚠️ [PartnerViewController] Failed to set UI context:', err);
      }

      this.notifyListeners();
    } catch (err) {
      logger.error('Failed to fetch partner data:', err);
      this.replaceState({
        error: 'Не удалось загрузить данные партнера',
        loading: false,
      });
      if (!isModal) {
        setTabTitle(`Партнер #${partnerId} - Ошибка`);
      }
      this.notifyListeners();
    }
  }

  /**
   * Проверить права доступа для текущего партнера
   */
  async checkPermissions(
    partnerId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    if (!partnerId || isNaN(partnerId)) {
      this.replaceState({
        permissions: {
          canEdit: false,
          canDelete: false,
          canArchive: false,
          canEditManager: false,
          canMerge: false,
          loading: false,
        },
      });
      this.notifyListeners();
      return;
    }

    try {
      this.replaceState({
        permissions: {
          ...this.state.permissions,
          loading: true,
        },
      });
      this.notifyListeners();

      // Проверяем права на редактирование, удаление и точечно — на архивацию
      // и смену менеджера. Точечные права считает сервер: базовый CRM их не
      // ограничивает, а инстанс может навесить свой guard через onCanUpdate
      // с нужным field (см. grasias/server/rules/partner_access_rules.ts).
      const [
        canUpdateResult,
        canDeleteResult,
        canArchiveResult,
        canEditManagerResult,
        canMergeResult,
      ] = await Promise.all([
        partnerRepository.canUpdate(partnerId),
        partnerRepository.canDelete(partnerId),
        partnerRepository.canUpdate(partnerId, 'archived'),
        partnerRepository.canUpdate(partnerId, 'manager'),
        partnerRepository.can('mergeDuplicates'),
      ]);

      this.replaceState({
        permissions: {
          canEdit: canUpdateResult.allowed,
          canDelete: canDeleteResult.allowed,
          canArchive: canArchiveResult.allowed,
          canEditManager: canEditManagerResult.allowed,
          canMerge: canMergeResult.allowed,
          loading: false,
        },
      });
      this.notifyListeners();
    } catch (err) {
      logger.error('Failed to check permissions:', err);
      // При ошибке запрещаем действия (fail-secure)
      this.replaceState({
        permissions: {
          canEdit: false,
          canDelete: false,
          canArchive: false,
          canEditManager: false,
          canMerge: false,
          loading: false,
        },
      });
      this.notifyListeners();
    }
  }

  /**
   * Обновить контактные каналы
   */
  async refreshContacts(
    partnerId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    if (!partnerId || isNaN(partnerId)) {
      return;
    }

    try {
      logger.info('🔄 [PartnerViewController] Refreshing channels for partner:', partnerId);

      const contactChannelsResult = await partnerRepository.getPartnerContacts(partnerId);
      logger.info('📋 [PartnerViewController] Updated channels:', contactChannelsResult);

      this.replaceState({
        contactChannels: contactChannelsResult || [],
      });
      this.notifyListeners();
    } catch (error) {
      logger.error('❌ [PartnerViewController] Error refreshing channels:', error);
    }
  }

  /**
   * Обновить контактные лица
   */
  async refreshContactPersons(
    partnerId: number,
    contactPersonRepository: AwaitableRpcClient<ContactPersonRepository>
  ): Promise<void> {
    if (!partnerId || isNaN(partnerId)) {
      return;
    }

    try {
      logger.info('🔄 [PartnerViewController] Refreshing contact persons for partner:', partnerId);

      const contactPersonsResult = await contactPersonRepository
        .filterByPartner(partnerId)
        .withContactChannels()
        .withContactChannelList()
        .orderBy('is_active', 'desc')
        .orderBy('is_primary', 'desc')
        .orderBy('name', 'asc')
        .fetch();
      logger.info('📋 [PartnerViewController] Updated contact persons:', contactPersonsResult);

      this.replaceState({
        contactPersons: contactPersonsResult || [],
      });
      this.notifyListeners();
    } catch (error) {
      logger.error('❌ [PartnerViewController] Error refreshing contact persons:', error);
    }
  }

  /**
   * Обновить сегменты партнёра.
   */
  async refreshSegments(
    partnerId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    if (!partnerId || isNaN(partnerId)) {
      return;
    }

    try {
      const segmentsResult = await partnerRepository.getSegments(partnerId);
      this.replaceState({
        segments: segmentsResult || [],
      });
      this.notifyListeners();
    } catch (error) {
      logger.error('❌ [PartnerViewController] Error refreshing segments:', error);
    }
  }

  /**
   * Обновить теги партнёра.
   */
  async refreshTags(
    partnerId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    if (!partnerId || isNaN(partnerId)) {
      return;
    }

    try {
      const tagsResult = await partnerRepository.getTags(partnerId);
      this.replaceState({
        tags: tagsResult || [],
      });
      this.notifyListeners();
    } catch (error) {
      logger.error('❌ [PartnerViewController] Error refreshing tags:', error);
    }
  }

  /**
   * Назначить сегмент партнёру.
   */
  async assignSegment(
    partnerId: number,
    segmentId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    await partnerRepository.assignSegment(partnerId, segmentId);
    await this.refreshSegments(partnerId, partnerRepository);
  }

  /**
   * Снять сегмент с партнёра.
   */
  async removeSegment(
    partnerId: number,
    segmentId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    await partnerRepository.removeSegment(partnerId, segmentId);
    await this.refreshSegments(partnerId, partnerRepository);
  }

  /**
   * Назначить тег партнёру.
   */
  async assignTag(
    partnerId: number,
    tagId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    await partnerRepository.assignTag(partnerId, tagId);
    await this.refreshTags(partnerId, partnerRepository);
  }

  /**
   * Снять тег с партнёра.
   */
  async removeTag(
    partnerId: number,
    tagId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    await partnerRepository.removeTag(partnerId, tagId);
    await this.refreshTags(partnerId, partnerRepository);
  }

  /**
   * Открыть модалку редактирования
   */
  openEdit(): void {
    this.replaceState({
      modals: {
        ...this.state.modals,
        edit: true,
      },
    });
    this.notifyListeners();
  }

  /**
   * Закрыть модалку редактирования
   */
  closeEdit(): void {
    this.replaceState({
      modals: {
        ...this.state.modals,
        edit: false,
      },
    });
    this.notifyListeners();
  }

  /**
   * Открыть модалку удаления
   */
  openDelete(): void {
    this.replaceState({
      modals: {
        ...this.state.modals,
        delete: true,
      },
    });
    this.notifyListeners();
  }

  /**
   * Обновить примечания партнёра в состоянии (без полной перезагрузки).
   */
  updatePartnerNotes(notes: string): void {
    if (!this.state.partner) return;
    this.replaceState({
      partner: { ...this.state.partner, notes },
    });
    this.notifyListeners();
  }

  /**
   * Закрыть модалку удаления
   */
  closeDelete(): void {
    this.replaceState({
      modals: {
        ...this.state.modals,
        delete: false,
      },
    });
    this.notifyListeners();
  }

  /**
   * Установить количество закупок партнёра (для управления видимостью вкладки).
   */
  setPurchasesCount(count: number): void {
    this.replaceState({ purchasesCount: count });
    this.notifyListeners();
  }

  /**
   * Удалить партнера и закрыть view (drawer или tab).
   * Возвращает null при успехе или сообщение об ошибке (для показа нотификации).
   * При ошибке модалка подтверждения остаётся открытой.
   */
  async deletePartner(
    partnerId: number,
    navigate: NavigateFunction,
    partnerRepository: AwaitableRpcClient<PartnerRepository>,
    isModal: boolean = false,
    closeDrawer?: () => void
  ): Promise<string | null> {
    if (!partnerId) {
      return null;
    }

    try {
      await partnerRepository.deleteById(partnerId);

      this.closeDelete();

      if (isModal && closeDrawer) {
        closeDrawer();
      } else {
        const { TabController } = await import('@ayocore/platform/client/tabs/TabController');
        TabController.getInstance().removeTab(`/crm/partners/${partnerId}`);
        navigate('/crm/partners');
      }

      return null;
    } catch (error) {
      logger.error('Failed to delete partner:', error);
      return extractErrorMessage(error, 'Не удалось удалить партнёра');
    }
  }

  /**
   * Обновить только данные партнёра в состоянии (без loading, без полной перезагрузки).
   * Используется после инлайн-редактирования адреса, региона и т.п.
   */
  async refreshPartner(
    partnerId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    const partnerResult = await partnerRepository.withRoleList().findById(partnerId);
    if (!partnerResult || !this.state.partner) return;
    this.replaceState({
      partner: partnerResult as PartnerExtended,
    });
    this.notifyListeners();
  }

  /**
   * Архивировать партнёра. Возвращает null при успехе или сообщение об ошибке.
   */
  async archivePartner(
    partnerId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<string | null> {
    try {
      await partnerRepository.update(partnerId, { archived_at: new Date() } as Partial<Partner>);
      await this.refreshPartner(partnerId, partnerRepository);
      return null;
    } catch (error) {
      logger.error('Failed to archive partner:', error);
      return error instanceof Error ? error.message : 'Не удалось архивировать партнёра';
    }
  }

  /**
   * Убрать партнёра из архива. Возвращает null при успехе или сообщение об ошибке.
   */
  async unarchivePartner(
    partnerId: number,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<string | null> {
    try {
      await partnerRepository.update(partnerId, { archived_at: null } as Partial<Partner>);
      await this.refreshPartner(partnerId, partnerRepository);
      return null;
    } catch (error) {
      logger.error('Failed to unarchive partner:', error);
      return error instanceof Error ? error.message : 'Не удалось убрать партнёра из архива';
    }
  }

  /**
   * Перезагрузить данные партнера (для использования после успешного редактирования)
   */
  async reload(
    partnerId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    partnerRepository: AwaitableRpcClient<PartnerRepository>,
    contactPersonRepository: AwaitableRpcClient<ContactPersonRepository>,
    leadRepository: AwaitableRpcClient<LeadRepository>,
    dealRepository: AwaitableRpcClient<DealRepository>
  ): Promise<void> {
    await this.load(
      partnerId,
      isModal,
      setTabTitle,
      navigate,
      partnerRepository,
      contactPersonRepository,
      leadRepository,
      dealRepository
    );
  }
}
